import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

const GAP = 6;
const MARGIN = 8;
const MIN_HEIGHT = 140;

const useFloatingPanel = (open, triggerRef, panelRef, { align = 'end', matchWidth = false, exactWidth = false, side = 'vertical', cap = Infinity } = {}) => {
  const [position, setPosition] = useState(null);

  const update = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;

    const rect = trigger.getBoundingClientRect();
    const width = exactWidth ? rect.width : Math.max(panel.offsetWidth, matchWidth ? rect.width : 0);
    const naturalHeight = panel.scrollHeight;
    const viewportRoom = Math.min(cap, window.innerHeight - MARGIN * 2);

    if (side === 'right') {
      const height = Math.min(naturalHeight, viewportRoom);
      setPosition({
        top: Math.min(Math.max(MARGIN, rect.top), window.innerHeight - MARGIN - height),
        left: Math.min(rect.right + GAP * 3, window.innerWidth - width - MARGIN),
        maxHeight: viewportRoom,
        placement: 'right'
      });
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN;
    const spaceAbove = rect.top - GAP - MARGIN;
    const placeBelow = naturalHeight <= spaceBelow || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(viewportRoom, Math.max(Math.min(MIN_HEIGHT, viewportRoom), placeBelow ? spaceBelow : spaceAbove));
    const height = Math.min(naturalHeight, maxHeight);

    const preferredLeft = align === 'end' ? rect.right - width : rect.left;
    const left = Math.min(Math.max(MARGIN, preferredLeft), window.innerWidth - width - MARGIN);
    const preferredTop = placeBelow ? rect.bottom + GAP : rect.top - GAP - height;
    const top = Math.min(Math.max(MARGIN, preferredTop), window.innerHeight - MARGIN - height);

    setPosition({
      top,
      left,
      maxHeight,
      minWidth: matchWidth ? rect.width : undefined,
      width: exactWidth ? rect.width : undefined,
      placement: placeBelow ? 'bottom' : 'top'
    });
  }, [triggerRef, panelRef, align, matchWidth, exactWidth, side, cap]);

  useLayoutEffect(() => {
    if (open) update();
    else setPosition(null);
  }, [open, update]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, update]);

  const style = position
    ? { top: position.top, left: position.left, maxHeight: position.maxHeight, minWidth: position.minWidth, width: position.width }
    : { top: 0, left: 0, visibility: 'hidden' };

  return { style, placement: position ? position.placement : null, ready: Boolean(position) };
};

export default useFloatingPanel;
